import {
    AiGoalFilterUserType,
    AiGoalStatus,
    AiGoalType,
    AiGoalUpdateType,
    GoalSuggestion,
    userType,
} from "../../DTO";
import { Api } from "../api";

const convertGoal = (data: any): AiGoalType => ({
    id: data?.id,
    userId: data?.user_id,
    user: data?.user ?? null,
    type: data?.type,
    weekId: data?.week_id ?? null,
    dayId: data?.day_id ?? null,
    description: data?.description ?? null,
    recommendedAction: data?.recommended_action ?? null,
    focusArea: data?.focus_area ?? null,
    metricTarget: data?.metric_target ?? null,
    reason: data?.reason ?? null,
    suggestions: data?.suggestions ?? null,
    aiUsed: data?.ai_used ?? false,
    source: data?.source ?? null,
    status: data?.status,
    approvedBy: data?.approved_by ?? null,
    approvedAt: data?.approved_at ?? null,
    createdAt: data?.created_at,
});

export const GetUserGoals = (
    user: userType,
    userId: number,
): Promise<{ goals: AiGoalType[] }> => {
    return new Promise((resolve, reject) => {
        new Api({
            method: "get",
            path: `plan/goals/user/${userId}`,
            header: { Authorization: `Bearer ${user.access_token}` },
        })
            .call()
            .then((data: any) =>
                resolve({ goals: (data.data ?? []).map(convertGoal) }),
            )
            .catch((err) => reject(err?.data?.message ?? "Failed to fetch goals"));
    });
};

export const ApprovePendingGoal = (
    user: userType,
    goalId: number,
): Promise<void> => {
    return new Promise((resolve, reject) => {
        new Api({
            method: "post",
            path: `plan/goal/${goalId}/approve`,
            header: { Authorization: `Bearer ${user.access_token}` },
        })
            .call()
            .then(() => resolve())
            .catch((err) => reject(err?.data?.message ?? "Failed to approve goal"));
    });
};

export const RejectPendingGoal = (
    user: userType,
    goalId: number,
): Promise<void> => {
    return new Promise((resolve, reject) => {
        new Api({
            method: "post",
            path: `plan/goal/${goalId}/reject`,
            header: { Authorization: `Bearer ${user.access_token}` },
        })
            .call()
            .then(() => resolve())
            .catch((err) => reject(err?.data?.message ?? "Failed to reject goal"));
    });
};

export const UpdateWeekGoalSuggestions = (
    user: userType,
    goalId: number,
    suggestions: GoalSuggestion[],
): Promise<void> => {
    return new Promise((resolve, reject) => {
        new Api({
            method: "put",
            path: `plan/goal/${goalId}/suggestions`,
            header: { Authorization: `Bearer ${user.access_token}` },
            body: { suggestions },
        })
            .call()
            .then(() => resolve())
            .catch((err) => reject(err?.data?.message ?? "Failed to update suggestions"));
    });
};

export const UpdateGoal = (
    user: userType,
    goalId: number,
    updates: AiGoalUpdateType,
): Promise<AiGoalType> => {
    return new Promise((resolve, reject) => {
        new Api({
            method: "put",
            path: `plan/goal/${goalId}`,
            header: { Authorization: `Bearer ${user.access_token}` },
            body: {
                description: updates.description,
                recommended_action: updates.recommendedAction,
                focus_area: updates.focusArea,
                metric_target: updates.metricTarget,
                reason: updates.reason,
                suggestions: updates.suggestions,
            },
        })
            .call()
            .then((data: any) => resolve(convertGoal(data.data)))
            .catch((err) =>
                reject(err?.data?.message ?? "Failed to update goal"),
            );
    });
};

export const GetGoals = (
    user: userType,
    filters: {
        page?: number;
        perPage?: number;
        status?: AiGoalStatus;
        type?: "day" | "week";
        userId?: number;
    },
): Promise<{
    goals: AiGoalType[];
    users: AiGoalFilterUserType[];
    total: number;
    currentPage: number;
    lastPage: number;
}> => {
    return new Promise((resolve, reject) => {
        new Api({
            method: "get",
            path: "plan/goals",
            header: { Authorization: `Bearer ${user.access_token}` },
            params: {
                page: filters.page ?? 1,
                per_page: filters.perPage ?? 20,
                status: filters.status,
                type: filters.type,
                user_id: filters.userId,
            },
        })
            .call()
            .then((data: any) => {
                const result = data.data ?? {};
                resolve({
                    goals: (result.data ?? []).map(convertGoal),
                    users: (result.users ?? []).map((item: any) => ({
                        id: item.id,
                        displayId: item.display_id,
                        name: item.name,
                        email: item.email,
                        pendingCount: item.pending_count ?? 0,
                        approvedCount: item.approved_count ?? 0,
                    })),
                    total: result.total ?? 0,
                    currentPage: result.current_page ?? 1,
                    lastPage: result.last_page ?? 1,
                });
            })
            .catch((err) =>
                reject(err?.data?.message ?? "Failed to fetch goals"),
            );
    });
};

export const GetPendingGoals = (
    user: userType,
    page = 1,
    perPage = 15,
): Promise<{ goals: AiGoalType[]; total: number; currentPage: number; lastPage: number }> => {
    return new Promise((resolve, reject) => {
        new Api({
            method: "get",
            path: "plan/goals/pending",
            header: { Authorization: `Bearer ${user.access_token}` },
            params: { page, per_page: perPage },
        })
            .call()
            .then((data: any) => {
                const d = data.data;
                resolve({
                    goals: (d.data ?? []).map(convertGoal),
                    total: d.total ?? 0,
                    currentPage: d.current_page ?? 1,
                    lastPage: d.last_page ?? 1,
                });
            })
            .catch((err) => reject(err?.data?.message ?? "Failed to fetch pending goals"));
    });
};
